import type { Signal } from '@preact/signals-react'
import { batch, signal } from '@preact/signals-react'
import { uniqueElements } from '@susu/array'
import type { Locale } from '@susu/headless-commerce/config/locale'
import type { Campaign } from '@susu/headless-commerce/features/configurator/domain/Campaign/Campaign'
import type { Option } from '@susu/headless-commerce/features/configurator/domain/Option/Option'
import type { Preload } from '@susu/headless-commerce/features/configurator/domain/Preload/Preload'
import { Preview } from '@susu/headless-commerce/features/configurator/domain/Preview/Preview'
import { Selection } from '@susu/headless-commerce/features/configurator/domain/Selection/Selection'
import type { Step } from '@susu/headless-commerce/features/configurator/domain/Step/Step'
import type { CountryConfiguration } from '@susu/headless-commerce/types/CountryConfiguration'
import log from '@susu/headless-commerce/utils/log'
import { isNotNull } from '@susu/null'
import { isNotUndefined, isUndefined } from '@susu/undefined'

export class Configurator {
  allSteps: Step[]
  campaigns: Signal<Array<Campaign>>
  country: Signal<CountryConfiguration>
  currentStep: Signal<Step | undefined>
  currentStepIndex: Signal<number>
  defaultCampaign: Signal<Campaign | undefined>
  locale: Signal<Locale>
  modelId: Signal<string>
  orderedOptions: Signal<Option[]>
  pageType: Signal<string>
  preload: Signal<Preload | undefined>
  preview: Signal<Preview>
  previousStepName: Signal<string | undefined>
  searchParams: Signal<URLSearchParams>
  selectedCampaign: Signal<Campaign | undefined>
  selectedOption: Signal<Option | undefined>
  selection: Signal<Selection>

  constructor(
    country: CountryConfiguration,
    locale: Locale,
    searchParams: URLSearchParams,
    campaigns: Array<Campaign>,
    modelId: string,
    pageType: string,
    preload: Preload | undefined,
    steps: Array<Step>,
  ) {
    this.allSteps = steps
    this.campaigns = signal(campaigns)
    this.country = signal(country)
    this.currentStep = signal(undefined)
    this.currentStep.subscribe((newValue) => {
      log.debug({
        method: 'Configurator.currentStep.subscribe',
        name: newValue?.name.value,
        value: newValue,
      })
    })
    this.currentStepIndex = signal(0)
    this.defaultCampaign = signal(undefined)
    this.locale = signal(locale)
    this.modelId = signal(modelId)
    this.orderedOptions = signal([])
    this.pageType = signal(pageType)
    this.preload = signal(preload)
    this.preview = signal(new Preview())
    this.previousStepName = signal(undefined)
    this.searchParams = signal(searchParams)
    this.selectedCampaign = signal(undefined)
    this.selectedOption = signal(undefined)
    this.selection = signal(new Selection({}, {}, {}, {}))
  }

  async initialize() {
    log.debug({
      method: 'Configurator.initialize',
    })

    this.selection.value.updateWithDefaultOptions(this.allSteps)
    if (isNotUndefined(this.preload.value)) {
      this.selection.value.updateWithPreload(this.preload.value)
      this.selection.value.updateRecommended(this)

      this.currentStepIndex = signal(this.initializeCurrentStepIndex())
      this.currentStep = signal(
        this.getStepByIndex(this.currentStepIndex.value),
      )

      const firstOption = this.initializeFirstOption()
      this.selectedOption = signal(firstOption)

      this.selection.value.default.value =
        this.selection.value.removeHiddenSteps(
          this,
          this.selection.value.default.value,
        )
      this.selection.value.current.value =
        this.selection.value.removeHiddenSteps(
          this,
          this.selection.value.current.value,
        )

      if (!this.isOneStepOutOfStock()) {
        this.selection.value.default.value =
          this.selection.value.ensureInStockSelection(
            this,
            this.selection.value.default.value,
          )
        this.selection.value.current.value =
          this.selection.value.ensureInStockSelection(
            this,
            this.selection.value.current.value,
          )
      }
    } else {
      this.selection.value.updateRecommended(this)
      this.selection.value.updateWithRecommendations()

      this.currentStepIndex = signal(this.initializeCurrentStepIndex())
      this.currentStep = signal(this.steps.value[this.currentStepIndex.value])

      const firstOption = this.initializeFirstOption()
      this.selectedOption = signal(firstOption)
      this.selection.value.update(this, firstOption)
    }

    this.updateSelectedCampaign()
    this.defaultCampaign.value = this.selectedCampaign.value

    if (isUndefined(this.currentStep.value)) {
      throw new Error('Configurator.initialize: No current step')
    }

    this.orderedOptions = signal(this.currentStep.value.options.value)
    this.updateOrderedOptions()

    this.preview.value.updateImageProductIds(this.steps.value)
    await this.preview.value.update(
      this,
      this.currentStep.value,
      this.selection.value,
      this.modelId.value,
      this.country.value,
      this.locale.value,
    )

    log.debug({
      method: 'Configurator.initialize:complete',
    })
  }

  overrideWithPreload(preload: Preload) {
    log.debug({
      method: 'Configurator.overrideWithPreload',
    })

    this.selection.value.default.value = preload.selection.value
    this.selection.value.current.value = preload.selection.value
  }

  // Filters the steps by visibility.
  get steps(): Signal<Step[]> {
    const filteredSteps = this.allSteps.filter((step) => step.isVisible())
    return signal(filteredSteps)
  }

  set steps(steps: Step[]) {
    log.debug({
      method: 'Configurator.set steps',
      steps,
    })

    this.allSteps = steps
  }

  isOneStepOutOfStock() {
    for (const step of this.steps.value) {
      if (step.isOutOfStock()) {
        log.debug({
          method: 'Configurator.isOneStepOutOfStock',
          stepName: step.name.value,
          isOutOfStock: step.isOutOfStock(),
        })

        return true
      }
    }
    return false
  }

  getProductIds() {
    const productIds = []
    for (const step of this.steps.value) {
      for (const option of step.options.value) {
        if (option.product.value.id.value) {
          productIds.push(option.product.value.id.value)
        }
      }
    }

    log.debug({
      method: 'Configurator.getProductIds',
      productIds,
    })

    return productIds
  }

  getSummaryURL(summaryURL: string) {
    const url = new URL(window.location.href)

    const encoded = this.selection.value.encodeToQueryString(
      this.selection.value.current.value,
    )

    url.pathname = summaryURL
    url.searchParams.set('ids', encoded)

    const result = decodeURIComponent(url.toString())

    log.debug({
      method: 'Configurator.getSummaryURL',
      result,
    })

    return result
  }

  initializeCurrentStepIndex() {
    let result = 0

    if (
      isNotUndefined(this.preload.value) &&
      isNotUndefined(this.preload.value.step)
    ) {
      const step = this.findStepIndexByStepName(this.preload.value.step.value)
      if (isNotUndefined(step)) {
        result = step
        return step
      }
    }

    log.debug({
      method: 'Configurator.initializeCurrentStepIndex',
      result,
    })

    return result
  }

  initializeFirstOption() {
    log.debug({
      method: 'Configurator.initializeFirstOption',
    })

    if (isUndefined(this.currentStep.value)) {
      throw new Error('Configurator#initializeFirstOption: No current step')
    }

    if (isNotUndefined(this.preload.value)) {
      const stepName = this.currentStep.value.name.value
      const productId = this.preload.value.selection.value[stepName]
      const step = this.findStepByName(stepName)

      if (isNotUndefined(step) && isNotNull(productId)) {
        const option = step.findOptionByProductId(productId)
        if (isNotUndefined(option)) {
          return option
        }
      }
    }

    for (const option of this.currentStep.value.options.value) {
      const productId = option.product.value.id.value
      const currentStepName = this.currentStep.value.name.value
      const found =
        productId === this.selection.value.current.value[currentStepName]
      if (isNotUndefined(found)) {
        return option
      }
    }

    throw new Error('First option not found')
  }

  /**
   * Returns true if the current step is the first step in the configurator.
   * @returns boolean
   */
  isCurrentStepTheFirstStep() {
    const result = this.currentStepIndex.value === 0

    log.debug({
      method: 'Configurator.isCurrentStepTheFirstStep',
      result,
    })

    return result
  }

  /**
   * Returns true if the current step is the last step in the configurator.
   * @returns boolean
   */
  isCurrentStepTheLastStep() {
    const result = this.currentStepIndex.value === this.steps.value.length - 1

    log.debug({
      method: 'Configurator.isCurrentStepTheLastStep',
      result,
    })

    return result
  }

  updateCurrentStep() {
    log.debug({
      method: 'Configurator.updateCurrentStep',
      steps: this.steps.value,
      currentStepIndex: this.currentStepIndex.value,
      currentStep: this.currentStep.value,
    })

    this.currentStep.value = this.steps.value[this.currentStepIndex.value]

    log.debug({
      method: 'Configurator.updateCurrentStep:after',
      steps: this.steps.value,
      currentStepIndex: this.currentStepIndex.value,
      currentStep: this.currentStep.value,
    })
  }

  findStepByName(stepName: string) {
    let result: Step | undefined

    for (const step of this.allSteps) {
      if (step.name.value === stepName) {
        result = step
        break
      }
    }

    log.debug({
      method: 'Configurator.findStepByName',
      stepName,
      result,
    })

    return result
  }

  findStepIndexByStepName(stepName: string) {
    let result: number | undefined

    for (const [index, step] of this.steps.value.entries()) {
      if (step.name.value === stepName) {
        result = index
        break
      }
    }

    log.debug({
      method: 'Configurator.findStepIndexByStepName',
      stepName,
    })

    return result
  }

  findCampaignById(campaignId: string) {
    for (const campaign of this.campaigns.value) {
      if (campaign.id.value === campaignId) {
        return campaign
      }
    }
  }

  // TODO: Rewrite imperatively.
  updateOrderedOptions() {
    log.debug({
      method: 'Configurator.updateOrderedOptions',
    })

    if (isUndefined(this.currentStep.value)) {
      throw new Error('Configurator.updateOrderedOptions: No current step')
    }

    this.orderedOptions.value = [
      ...this.currentStep.value.options.value.filter((option) =>
        this.selection.value.isOptionRecommended(option),
      ),
      ...this.currentStep.value.options.value.filter(
        (option) => !this.selection.value.isOptionRecommended(option),
      ),
    ]
  }

  updateSelectedCampaign() {
    log.debug({
      method: 'Configurator.updateSelectedCampaign',
    })

    for (const step of this.allSteps) {
      for (const option of step.allOptions) {
        if (isNotUndefined(option.campaignId.value)) {
          if (this.selection.value.isOptionSelected(option)) {
            const campaign = this.findCampaignById(option.campaignId.value)
            if (isNotUndefined(campaign)) {
              this.selectedCampaign.value = campaign
              return
            }
          }
        }
      }
    }
  }

  async selectOption(newSelectedOption: Option) {
    log.debug({
      method: 'Configurator.selectOption',
      newSelectedOption,
    })

    await batch(async () => {
      if (isUndefined(this.currentStep.value)) {
        throw new Error('selectOption: No current step')
      }

      this.selectedOption.value = newSelectedOption
      this.selection.value.update(this, this.selectedOption.value)
      this.updateOrderedOptions()
      this.updateSelectedCampaign()
      await this.preview.value.update(
        this,
        this.currentStep.value,
        this.selection.value,
        this.modelId.value,
        this.country.value,
        this.locale.value,
      )

      log.debug({
        method: 'Configurator.selectOption:complete',
      })
    })
  }

  getStepByIndex(stepIndex: number) {
    const step = this.steps.value[stepIndex]

    if (isUndefined(step)) {
      throw new Error('Configurator.getStepByIndex: No step at index')
    }

    log.debug({
      method: 'Configurator.getStepByIndex',
      stepIndex,
      step,
    })

    return step
  }

  async navigateToStep(nextStepIndex: number) {
    const nextStep = this.getStepByIndex(nextStepIndex)
    const nextStepName = nextStep.name.value

    const currentURL = new URL(window.location.toString())
    currentURL.searchParams.set('step', nextStepName)

    log.debug({
      method: 'Configurator.navigateToStep',
      nextStepIndex,
      nextStepName,
    })

    await batch(async () => {
      if (isUndefined(this.currentStep.value)) {
        throw new Error('navigateToStep: No current step')
      }

      this.previousStepName.value = this.currentStep.value.name.value
      this.currentStepIndex.value = nextStepIndex
      this.updateCurrentStep()
      this.updateOrderedOptions()
      await this.preview.value.update(
        this,
        this.currentStep.value,
        this.selection.value,
        this.modelId.value,
        this.country.value,
        this.locale.value,
      )

      // Tracking helper
      const productId =
        this.selection.value.current.value[this.currentStep.value.name.value]
      if (isNotNull(productId)) {
        this.selectedOption.value = this.orderedOptions.value[0]
        const option = this.currentStep.value.findOptionByProductId(productId)
        if (isNotUndefined(option)) {
          this.selectedOption.value = option
        }
      }

      log.debug({
        method: 'Configurator.navigateToStep:complete',
        currentStepIndex: this.currentStepIndex.value,
        previousStepName: this.previousStepName.value,
      })
    })
  }

  getNextStepIndex() {
    const nextStepIndex = Math.min(
      this.currentStepIndex.value + 1,
      this.steps.value.length - 1,
    )

    log.debug({
      method: 'Configurator.getNextStepIndex',
      nextStepIndex,
    })

    return nextStepIndex
  }

  getNextStepName() {
    const nextStepIndex = this.getNextStepIndex()
    const nextStep = this.getStepByIndex(nextStepIndex)

    log.debug({
      method: 'Configurator.getNextStepName',
      nextStepIndex,
      nextStep,
    })

    return nextStep.name.value
  }

  async navigateToNextStep() {
    const nextStepIndex = this.getNextStepIndex()

    log.debug({
      method: 'Configurator.navigateToNextStep',
      nextStepIndex,
    })

    await this.navigateToStep(nextStepIndex)
  }

  getPreviousStepIndex() {
    const previousStepIndex = Math.max(0, this.currentStepIndex.value - 1)

    log.debug({
      method: 'Configurator.getPreviousStepIndex',
      previousStepIndex,
    })

    return previousStepIndex
  }

  getPreviousStepName() {
    const previousStepIndex = this.getPreviousStepIndex()
    const previousStep = this.getStepByIndex(previousStepIndex)

    log.debug({
      method: 'Configurator.getPreviousStepName',
      previousStepIndex,
      previousStep,
    })

    return previousStep.name.value
  }

  async navigateToPreviousStep() {
    const previousStepIndex = this.getPreviousStepIndex()

    log.debug({
      method: 'Configurator.navigateToPreviousStep',
      previousStepIndex,
    })

    await this.navigateToStep(previousStepIndex)
  }

  handleFinish(summaryURL: string) {
    log.debug({
      method: 'Configurator.handleFinish',
      summaryURL,
    })

    window.location.href = this.getSummaryURL(summaryURL)
  }

  getCampaignIds() {
    const campaignIds: string[] = []
    for (const step of this.allSteps) {
      campaignIds.push(...step.getCampaignIds())
    }

    const result = uniqueElements(campaignIds)

    log.debug({
      method: 'Configurator.getCampaignIds',
      result,
    })

    return result
  }
}
